import {Link} from 'react-router-dom';
import {Search, ShieldCheck, CircleQuestionMark, CreditCard, UserCog} from 'lucide-react';
import { CARD_DATA } from './404NotFound';
import {FaqSection, QUESTION_AND_ANSWER, QuestionAnswer} from '../data/FaqData';
import {useState} from "react";
import {Typography} from "../components/ui/Typography";
import {ContactModal} from "../components/modals/ContactModal";
import {Button} from "../components/ui/Button";

export const PAGE_CARDS: CARD_DATA[] = [
    {
        id:'0',
        title: FaqSection.General,
        pageUrl: "#" + FaqSection.General,
        icon: CircleQuestionMark,
    },
    {
        id: '1',
        title: FaqSection.Billing,
        pageUrl: "#" + FaqSection.Billing,
        icon: CreditCard,
    },
    {
        id: '2',
        title: FaqSection.Licensing,
        pageUrl: "#" + FaqSection.Licensing,
        icon: ShieldCheck,
    },
    {
        id: '3',
        title: FaqSection.Account,
        pageUrl: "#" + FaqSection.Account,
        icon: UserCog,
    },
];

export const Faq = (any) => {
    return (
        <section id="help-hub" className="py-20 sm:py-24 lg:py-32">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <HelpCenterHub/>
                <QASection/>
                <ContactSection/>
            </div>
        </section>
    );
};

export const HelpCenterHub = (any) => {
    return (
        <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold 
            tracking-tight text-surface-50">
                How can we help?
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-surface-300">
                Have a question? We're here to help. Search our articles or browse
                by category to find the answers you need.
            </p>

            <SearchBar/>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
                {PAGE_CARDS.map(cardData => <PageCard data={cardData}/>)}
            </div>
        </div>
    );
};

export const SearchBar = (any) => {
    return (
        <div className="mt-10 max-w-2xl mx-auto">
            <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <Search className="h-5 w-5 text-surface-300"/>
                </div>
                <input type="search" placeholder="Search for answers..."
                       className="w-full rounded-lg border border-surface-800 bg-surface-900
                        py-3.5 pl-11 pr-4 text-surface-50 placeholder:text-surface-300
                        focus:outline-none focus:ring-2 focus:ring-[var(--accent-secondary)] 
                        focus:border-[var(--accent-secondary)]"/>

            </div>
        </div>
    );
};


export const PageCard = ({data}) => {
    const {title, pageUrl, icon: IconComponent} = data as CARD_DATA;
    return (
        <Link
            to={pageUrl}
            className="group flex flex-col items-start justify-center gap-3 rounded-xl border p-6 text-left 
            transition-all duration-200 bg-surface-900 border-surface-800 
            hover:-translate-y-1 hover:border-brand-500 hover:shadow-xl hover:shadow-black/20"
        >

            <IconComponent
                className="h-8 w-8 text-brand-500"
            />

            <span className="font-semibold text-sm text-surface-50">{title}</span>
        </Link>
    );
};

const groupedQAs = QUESTION_AND_ANSWER.reduce((acc, qa) => {
    if (!acc[qa.section]) {
        acc[qa.section] = [];
    }
    acc[qa.section].push(qa);
    return acc;
}, {} as Record<FaqSection, QuestionAnswer[]>);

export const QASection = () => {
    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            {Object.keys(groupedQAs).map((sectionKey) => {
                const section = sectionKey as FaqSection;
                const qas = groupedQAs[section];
                return (
                    <section 
                        id={section.toLowerCase()} 
                        className="py-12 scroll-mt-36"
                    >
                        <h2 className="text-3xl font-bold tracking-tight text-surface-50">
                            {getSectionTitle(section)}
                        </h2>
                        <div className="mt-8 space-y-2">
                            {qas.map((qa, index) => (
                                <QA key={index} data={qa}/>
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export const QA = ({data}: { data: QuestionAnswer }) => {
    const {question, answer} = data;
    return (
        <details className="border-b border-surface-800 py-6 group">
            <summary className="flex justify-between items-center cursor-pointer list-none">
                <span
                    className="font-semibold text-lg text-surface-50 
                    group-hover:text-brand-500 transition-colors">
                    {question}
                </span>
                <i data-lucide="chevron-down"
                   className="h-5 w-5 text-surface-300 transition-transform 
                   duration-300 group-open:rotate-180"></i>
            </summary>
            <div className="mt-4 text-base leading-relaxed">
                {answer}
            </div>
        </details>
    );
};

export const ContactSection = (any) => {

    const [isContactModalOpen, setContactModal] = useState(false);
    function toggleContactModal() {
        setContactModal(prev => !prev); 
    }
    
    return (
        <section className="py-20 sm:py-24">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-surface-900 border border-surface-800 rounded-2xl p-8 md:p-12 lg:p-16 text-center">
                    
                    <Typography as="h2" variant="h2">
                        Can't find what you're looking for?
                    </Typography>

                    <Typography as="p" variant="p" className="mt-4 max-w-xl mx-auto text-lg">
                        Our support team is always here to help. Reach out to us and we'll
                        get back to you as soon as possible.
                    </Typography>
                    
                    <div className="flex items-center justify-center mt-8">
                        <Button 
                            intent="primary"
                            size="medium"
                            onClick={toggleContactModal}
                        >
                            Contact Support
                        </Button>
                    </div>
                    
                    <ContactModal 
                        isOpen={isContactModalOpen} 
                        onClose={() => toggleContactModal()} 
                    />
                    
                </div>
            </div>
        </section>
    );
};


function getSectionTitle(section: FaqSection): string {
    switch (section) {
        case FaqSection.General:
            return 'General Questions';
        case FaqSection.Billing:
            return 'Billing & Credits';
        case FaqSection.Licensing:
            return 'Licensing';
        case FaqSection.Account:
            return 'Account';
        default:
            return section;
    }
}




























